// ============================================================================
// src/propositionsProjet.js : la fiche projet ouverte et ce que le serveur y
// écrit pendant qu'elle est ouverte (correctif du 20/10/2026)
// ============================================================================
// Signalé par Pierre : « les ajouts proposés par IA que j'ai validés dans les
// projets en fonction des comptes rendus ne s'inscrivent pas dans les fiches ».
// Cause : projetNootaApplierProposition écrivait bien la fiche, puis la
// fenêtre, ouverte AVANT le geste, l'enregistrait quelques secondes plus tard
// par saveProject, qui réécrit TOUT le document (setDoc) depuis l'état
// d'ouverture : le champ accepté repartait à sa valeur d'avant.
//
// Deux règles pures, tenues par src/propositionsProjet.test.js :
//   • `reprendreDansFormulaire` : ce que le serveur a écrit (`ecrit`, rendu par
//     projetNootaApplierProposition) entre dans le formulaire ouvert, ajouté à
//     ce qui est en cours de saisie sans doublon ;
//   • `fusionnerAvecBase` : à l'enregistrement, un champ que la fenêtre n'a PAS
//     touché depuis son ouverture garde la valeur de la base (écrite ailleurs
//     entre-temps : proposition acceptée, autre administrateur, engagement) ;
//     seul ce que la personne a changé réécrit la base.
// ============================================================================

/** Action du journal des propositions acceptées (miroir de
 *  functions/lib/projetPropositions.js, ACTION_JOURNAL) : trace en lecture
 *  seule, son « avant » ne porte qu'un champ. */
export const ACTION_PROPOSITION_REUNION = "proposition_reunion";

/** JSON aux clés triées (Firestore relit les cartes clés triées). */
export function stable(v) {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stable(v[k])}`).join(",")}}`;
  return JSON.stringify(v === undefined ? null : v);
}
export const egal = (a, b) => stable(a) === stable(b);
// À l'enregistrement, absent, null, "", [] et {} se valent : le formulaire
// pose ses valeurs par défaut sur un champ que la fiche n'avait pas.
const vide = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)
  || (!!v && typeof v === "object" && !Array.isArray(v) && !Object.keys(v).length);
const memeSaisie = (a, b) => (vide(a) && vide(b)) || egal(a, b);

// Gérés par l'enregistrement lui-même, jamais repris de la base.
const PROPRES_A_L_ENREGISTREMENT = new Set(["id", "dateMaj", "dateCreation"]);

/**
 * La fiche à écrire : la saisie, sauf pour un champ que la saisie n'a pas
 * changé depuis `base` (l'état d'ouverture) alors que `enBase` (la fiche relue
 * juste avant l'écriture) l'a vu changer : celui-là garde la valeur de la base.
 * Un champ apparu dans la base depuis l'ouverture est gardé ; un champ retiré
 * de la base, que la saisie n'a pas touché, n'est pas réécrit.
 * @returns {{ fiche: object, conserves: string[] }}
 */
export function fusionnerAvecBase(saisie, base, enBase) {
  if (!base || !enBase) return { fiche: saisie, conserves: [] };
  const fiche = {};
  const conserves = [];
  const cles = new Set([...Object.keys(saisie || {}), ...Object.keys(enBase || {})]);
  for (const k of cles) {
    const s = saisie[k];
    if (!PROPRES_A_L_ENREGISTREMENT.has(k) && memeSaisie(s, base[k]) && !memeSaisie(enBase[k], base[k])) {
      if (enBase[k] !== undefined) fiche[k] = enBase[k];
      conserves.push(k);
    } else if (s !== undefined) {
      fiche[k] = s;
    }
  }
  return { fiche, conserves };
}

/** Clé de doublon d'un élément de liste : son texte, sans balise ni casse. */
const texteDe = (x) => String(x == null ? "" : x).replace(/<[^>]+>/g, " ").replace(/\*\*/g, "")
  .normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const cleElement = (x) => (x && typeof x === "object" && typeof x.texte === "string" ? texteDe(x.texte)
  : typeof x === "string" ? texteDe(x) : stable(x));

/**
 * Le formulaire ouvert, une fois la proposition écrite par le serveur :
 * un ajout complète la liste EN COURS DE SAISIE (sans doublon), un
 * remplacement pose la valeur écrite. `ecrit` absent ou inchangé : rien.
 * @param {object} form  le formulaire de la fenêtre
 * @param {{ field: string, type: string, valeur: any, ajouts: any[]|null, inchange?: boolean }} ecrit
 */
export function reprendreDansFormulaire(form, ecrit) {
  if (!ecrit || !ecrit.field || ecrit.inchange) return form;
  if (ecrit.type === "append") {
    const cur = Array.isArray(form[ecrit.field]) ? form[ecrit.field] : [];
    const vus = new Set(cur.map(cleElement));
    const ajouts = (Array.isArray(ecrit.ajouts) ? ecrit.ajouts : []).filter((x) => {
      const k = cleElement(x);
      if (!k || vus.has(k)) return false;
      vus.add(k);
      return true;
    });
    return ajouts.length ? { ...form, [ecrit.field]: [...cur, ...ajouts] } : form;
  }
  return { ...form, [ecrit.field]: ecrit.valeur };
}

/** Le libellé d'un champ de la fiche (dialogue de confirmation). */
export const LIBELLES_CHAMPS = Object.freeze({
  description: "Description", contexte: "Contexte", objectifs: "Objectifs", methodologie: "Méthodologie",
  principes: "Principes", livrables: "Livrables", beneficesAttendus: "Bénéfices attendus", prochainesEtapes: "Étapes",
});
